import { Grid } from '@mui/material'
import ChevronBtn from 'components/Button/Chevron'
import useCollectionTemplate from 'hooks/useCollectionTemplate'
import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import carouselArrow from '/assets/Icon/Carousel-arrow.svg'
import { collectionIds } from 'utils/collectionIds'
import IndexCard from 'components/Card/IndexCard'
import PlaceholderCard from 'components/Card/PlaceholderCard'
import CarouselCoachCard from 'components/Card/CarouselCoachCard'

export interface MomentsCarouselProps {
  moments?: any
  collectionName?: keyof typeof collectionIds
  playerQty?: number
  slug?: string
  ctaLink?: string
  title: string
  reverse?: boolean
  filterByTeam?: string
  templateIds?: number[]
  displayCoachCard?: boolean
  coachTitle?: string
  coachDescription?: string
  coachCtaLink?: string
  returnFloorPrices?: boolean
}

const MomentsCarousel = ({
  moments,
  collectionName,
  slug,
  ctaLink,
  title,
  playerQty = 100,
  reverse = false,
  filterByTeam,
  templateIds,
  displayCoachCard,
  coachTitle,
  coachDescription,
  coachCtaLink,
  returnFloorPrices = false
}: MomentsCarouselProps) => {
  // if filterByTeam is passed we ignore collectionName
  const { momentTemplates } = useCollectionTemplate(
    collectionName,
    returnFloorPrices,
    templateIds
  )
  const scrollRef = useRef<HTMLDivElement>(null)
  const windowWidth = window.innerWidth
  const [scrollLeftBtn, setScrollLeftBtn] = React.useState(false)
  const [scrollRightBtn, setScrollRightBtn] = React.useState(true)
  const MIN_DISPLAY_ITEMS = displayCoachCard ? 5 : 6
  let placeholderArray = []
  const displayCoach =
    displayCoachCard && coachTitle && coachDescription && coachCtaLink

  const momentArray: any = React.useMemo(() => {
    if (moments)
      return moments.map((moment: any) => {
        return {
          floorPrice: moment.floorPrice,
          ...moment.templateData.immutableData,
          id: moment.id,
          templateId: moment.templateData.templateId,
          serial: moment.mintNumber
        }
      })
    else
      return momentTemplates.map((moment) => {
        return {
          floorPrice: moment.floorPrice,
          ...moment.templateData.immutableData,
          id: moment.id,
          templateId: moment.templateData.templateId
        }
      })
  }, [momentTemplates.length, moments?.length]).filter(
    // filter by team if filterByTeam is passed
    (moment: any) =>
      filterByTeam === undefined
        ? true
        : moment.teamOfInvolvedPlayer === filterByTeam
  )

  const displayArray = reverse ? [...momentArray].reverse() : momentArray
  const displayArrayLength = displayArray.length
  const numPlaceholders = Math.max(MIN_DISPLAY_ITEMS - displayArrayLength, 0)

  for (let i = 0; i < numPlaceholders; i++) {
    placeholderArray.push(<PlaceholderCard key={`placeholder-${i}`} />)
  }

  const scroll = (scrollOffset: number) => {
    if (scrollRef.current !== null) {
      scrollRef.current.scrollLeft += scrollOffset
    }
  }

  useEffect(() => {
    const carousel = scrollRef.current as HTMLDivElement

    const handleScroll = () => {
      if (carousel.scrollLeft + carousel.clientWidth === carousel.scrollWidth) {
        setScrollRightBtn(false)
      } else {
        setScrollRightBtn(true)
      }

      if (carousel.scrollLeft > 0) {
        setScrollLeftBtn(true)
      } else {
        setScrollLeftBtn(false)
      }
    }

    carousel.addEventListener('scroll', handleScroll)

    return () => {
      carousel.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div className="w-full global-padding">
      <Grid className="flex relative justify-center mx-auto max-w-[1920px] md:pt-6 pb-6 md:pb-10">
        <Grid container className="w-full">
          <Grid className="flex justify-start items-center mb-2 w-full">
            {slug || ctaLink ? (
              <Link
                to={ctaLink ? ctaLink : `/momentIndex/${slug}`}
                className="hover:drop-shadow-[0_0_2px_rgba(255,255,255,0.7)] transition-all duration-300 section-heading"
              >
                {title} <ChevronBtn sx={{ margin: '0 0 10px 0px' }} />
              </Link>
            ) : (
              <div className="hover:drop-shadow-[0_0_2px_rgba(255,255,255,0.7)] transition-all duration-300 section-heading">
                {title}
              </div>
            )}
          </Grid>
          <div
            ref={scrollRef}
            className="flex overflow-x-auto flex-nowrap justify-between -ml-2 w-[98%] ease-in-out scroll-smooth ripper-skippers-moments"
          >
            {displayCoach && (
              <CarouselCoachCard
                title={coachTitle}
                description={coachDescription}
                ctaLink={coachCtaLink}
              />
            )}
            {displayArray.slice(0, playerQty).map((player: any, i: number) => (
              <IndexCard
                key={i}
                thumbnail={player.thumbnail?.replaceAll('%20', '%2520')}
                headline={player.headline}
                player={player.player}
                id={player.templateId}
                isGenesisBall={player.ballColor ? true : false}
                teamOfInvolvedPlayer={player.teamOfInvolvedPlayer}
                tier={player.tier}
                badges={player.badges}
                serialNumber={player.serial}
                nftId={player.id}
                isOwned={moments ? true : false}
                playersJumperNumber={player.playersJumperNumber}
                floorPrice={player.floorPrice}
                noWrap={true}
                maxSerial={player?.maxSerial}
                isSignedMoment={player.isSignedMoment}
                issuedSupply={
                  player.issuedSupply
                }
                burntSupply={player.burntSupply}
                circulatingSupply={
                  player.circulatingSupply
                }
              />
            ))}
            {placeholderArray}
          </div>
        </Grid>
        {scrollLeftBtn && (
          <button
            className="hidden absolute bottom-[50%] translate-y-[50%] left-0 z-50 md:flex"
            onClick={() => scroll(-windowWidth)}
          >
            <img
              src={carouselArrow}
              alt=""
              className="hover:drop-shadow-[0_0_5px_rgba(255,255,255,0.7)] drop-shadow-[0_0_2px_rgba(0,0,0,1)] transition-all duration-300 rotate-180"
            />
          </button>
        )}
        {scrollRightBtn && (
          <button
            className="hidden absolute right-0 bottom-[50%] translate-y-[50%] z-50  md:flex"
            onClick={() => scroll(windowWidth)}
          >
            <img
              src={carouselArrow}
              alt=""
              className="hover:drop-shadow-[0_0_5px_rgba(255,255,255,0.7)] drop-shadow-[0_0_2px_rgba(0,0,0,1)] transition-all duration-300"
            />
          </button>
        )}
      </Grid>
    </div>
  )
}

export default MomentsCarousel
